<template>
    <div class="grid">
        <div class="col-2 sub-container">
            <h5>Attributes</h5>
            <block-attributes-listing :shared="props.shared" />
        </div>
        <div class="col-10 sub-container">
            <div class="layoutJSON mx-auto text-center">
                <div class="font-bold text-lg mt-1">Generated Formula:</div>
                <div class="my-3">
                    <div class="flex flex-wrap formula-details-container overflow-y-auto">
                        <span v-for="item in sortedLayout" :key="item" class="mr-1">{{ item.code ?? item.value }}</span>
                    </div>
                    <span v-show="sortedLayout.length <= 0" class="text-600"> No formula yet </span>
                </div>
                <Divider />
                <div>
                    <Button label="Reset" class="p-button-danger mr-2" @click="builderStore.resetLayout" />
                    <Button label="Check Formula Validity" class="p-button-plain" @click="checkFormulaValidity" />
                </div>
            </div>

            <div class="toolbar my-2">
                <div class="math-ops">
                    <div class="section-label">Mathematical Operators & Brackets</div>
                    <div class="buttons">
                        <Button class="p-button-success" label="+" @click="addItem('+')" />
                        <Button class="p-button-success" label="-" @click="addItem('-')" />
                        <Button class="p-button-success" label="*" @click="addItem('*')" />
                        <Button class="p-button-success" label="/" @click="addItem('/')" />
                        <Button class="p-button-success" label="(" @click="addItem('(')" />
                        <Button class="p-button-success" label=")" @click="addItem(')')" />
                    </div>
                </div>
                <div class="logical-ops">
                    <div class="section-label">Logical Operators</div>
                    <div class="buttons">
                        <Button class="p-button-success" label="==" @click="addItem('==')" />
                        <Button class="p-button-success" label=">=" @click="addItem('>=')" />
                        <Button class="p-button-success" label="<=" @click="addItem('<=')" />
                        <Button class="p-button-success" label="!=" @click="addItem('!=')" />
                    </div>
                </div>
                <div class="bracket">
                    <div class="buttons">
                        <Button class="p-button-success" label="Add Constant" @click="addConstant" />
                        <InputText maxlength = "255" placeholder="Input something.." v-model="state.constantVal" />
                        <small class="p-invalid" v-if="state.submittedConstant && !state.constantVal">Constant value cannot be empty.</small>
                    </div>
                </div>
            </div>
            <div class="grid-drop-layout my-2" @drop="droppedItem($event)" @dragover.prevent>
                <div v-if="state.layout?.length <= 0" class="text-center py-5">Drop something into this area</div>
                <!-- <div > -->
                <grid-layout v-else class="gridlines overflow-y-auto" :useCssTransforms="false" :layout="currentLayout" :colNum="colNum" :rowHeight="30" :isDraggable="true" :verticalCompact="true" :isResizable="false">
                    <grid-item class="grid-item" :class="{ 'active-item': itemActive(item) }" @moved="draggedGridItem" v-for="item in currentLayout" :static="item.static" :x="item.x" :y="item.y" :w="item.w" :h="item.h" :i="item.i" :key="item">
                        <span class="text" v-tooltip="item.type + ' : ' + (item.value ?? item.code)">
                            {{ item.value ?? 'A' + item.x + item.y }}
                        </span>
                        <span class="remove" @click="removeItem(item.i)">x</span>
                    </grid-item>
                </grid-layout>
                <!-- </div> -->
            </div>
            <br />
            <div class="layoutJSON my-auto text-center">
                <div class="font-bold text-lg">Formula Details:</div>
                <div class="my-3">
                    <div class="flex flex-wrap formula-details-container overflow-y-auto">
                        <span :class="['mr-1', item.type == 'value' ? 'font-bold' : '']" v-for="item in sortedLayout" :key="item">
                            {{ item.type == 'code' ? "'" + item.code + "'" : getOperatorValue(item.value) }}
                        </span>
                    </div>
                    <span v-show="sortedLayout.length <= 0" class="text-600"> Nothing to show at the moment </span>
                </div>
            </div>
        </div>
        <div class="buttons mx-auto mt-2">
            <Button label="Submit" class="p-button-help" @click="submitFormula" :disabled="props.disableSubmit" />
        </div>
    </div>
</template>

<script setup>
import { reactive, computed, defineProps, defineEmits } from 'vue';
import BlockAttributesListing from '@/components/block/BlockAttributesListing.vue';

import useFormulaBuilderComposable from '@/composables/FormulaBuilderComposable';
import useFormulaComposable from '@/composables/FormulaComposable';
import { useFormulaBuilderStore } from '../../store/formula/formulaBuilder';
import { storeToRefs } from 'pinia';

const emit = defineEmits(['submit-formula']);

const props = defineProps({
    disableSubmit: {
        type: Boolean,
    },
    shared: {
        type: String,
        default: 'Company',
    },
});
const builderStore = useFormulaBuilderStore();
const { activeItem } = storeToRefs(builderStore);
const { colNum, draggedAttribute, addItem, currentLayout, getOperatorValue, getTestFormulaBody } = useFormulaBuilderComposable();
const { testFormula } = useFormulaComposable();
const state = reactive({
    constantVal: null,
    submittedConstant: false,
});

const sortedLayout = computed(() => {
    let sortedData = currentLayout.value;

    // Sort the objects by the x property, in ascending order.
    sortedData.sort((a, b) => a.x - b.x);

    // Sort the objects by the y property, in ascending order, using a stable sort.
    return sortedData.sort((a, b) => a.y - b.y, { stable: true });
});

const draggedGridItem = (i, x, y) => {
    builderStore.setActiveItem({ i, x, y });
};

const droppedItem = (event) => {
    event.preventDefault();
    addItem(draggedAttribute.value?.code, 'code');
};

const addConstant = () => {
    state.submittedConstant = true;
    if (state.constantVal) {
        addItem(state.constantVal);
        state.submittedConstant = false;
        state.constantVal = null;
    }
};

const removeItem = (val) => {
    const index = currentLayout.value.map((item) => item.i).indexOf(val);
    currentLayout.value.splice(index, 1);
};

const checkFormulaValidity = async () => {
    const formulaBody = await getTestFormulaBody(sortedLayout.value);
    await testFormula(formulaBody);
};

const submitFormula = () => {
    emit('submit-formula');
};

const itemActive = (item) => {
    return item?.x === activeItem.value?.x && item?.y === activeItem.value?.y && item?.i === activeItem.value?.i;
};
</script>

<style>
.sub-container {
    height: 75vh;
    overflow-y: auto;
}

.p-divider.p-divider-horizontal:before {
    border: 1px solid rgb(199, 165, 165);
}
/** Toolbar section css */

.toolbar {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 10px;
}

.math-ops,
.logical-ops,
.bracket {
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid #ccc;
    padding: 10px;
}

.section-label {
    font-weight: bold;
    margin-bottom: 5px;
}

.buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
}

button {
    padding: 5px 10px;
    font-size: 16px;
}

.grid-drop-layout {
    background-color: rgb(187 221 250);
    height: 25vh;
}
/* Toolbar section css ends */

.layoutJSON {
    background-color: rgb(187 221 250);
    /* border: 1px solid black; */
    margin-top: 10px;
    padding: 10px;
}

.formula-details-container {
    max-height: 5rem;
}

.columns {
    -moz-columns: 120px;
    -webkit-columns: 120px;
    columns: 120px;
}

.remove {
    position: absolute;
    font-size: 14px;
    right: 2px;
    top: -5px;
    cursor: pointer;
    color: white;
    /* background-color: white; */
}

.vue-grid-layout {
    border: 1px solid black 0.5;
    /* background-color: rgb(187 221 250); */
}
.gridlines {
    height: inherit !important;
    border: 0.5px dashed black;
}

.vue-grid-item:not(.vue-grid-placeholder) {
    background: rgb(36 36 210);
    border: 1px solid black;
    border-radius: 5px;
}

.vue-grid-item {
    height: 30px !important;
    width: 60px !important;
}
.vue-grid-item .text {
    font-size: 15px;
    text-align: center;
    position: absolute;
    top: 8px;
    bottom: 0;
    left: -4px;
    right: 0;
    height: 100%;
    width: 100%;
    color: white;
    overflow-x: hidden;
    margin: auto;
}

.vue-grid-item .no-drag {
    height: 100%;
    width: 100%;
}

.vue-grid-item .add {
    cursor: pointer;
}

.active-item {
    border: 2px dashed yellow !important;
}
</style>
